/**
 * First-party referral-code cookie bridge for the BC360 Scale360 attribution flow.
 *
 * The "/join" plan page (LandingPage.tsx) and the embedded checkout
 * (Scale360Checkout.tsx) already thread ?ref=REPCODE straight into the Stripe
 * Checkout Session metadata (see server/billing.ts) and into lead creation.
 * That only works as long as the ref survives in the URL all the way through.
 * It doesn't survive a bookmark, a return visit days later, or a hop that
 * drops query params. This module is the "belt and suspenders" fix: capture
 * ?ref= into a 90-day first-party cookie the moment it's seen, and fall back
 * to that cookie whenever the current URL has no ref. The URL always wins
 * when present; an absent/expired cookie just falls through to organic
 * (no referral) — it never blocks a lead submission or a checkout.
 */

const WBZ_REF_COOKIE = "wbz_ref";
const WBZ_REF_MAX_AGE_SECONDS = 60 * 60 * 24 * 90; // 90 days

// Pure parsing, unit-testable without a DOM.
export function parseReferralCookie(cookieString: string): string {
  const match = cookieString.match(new RegExp(`(?:^|;\\s*)${WBZ_REF_COOKIE}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

// Pure cookie-string builder, unit-testable without a DOM.
export function buildReferralCookieString(ref: string): string {
  return `${WBZ_REF_COOKIE}=${encodeURIComponent(ref.trim())}; path=/; max-age=${WBZ_REF_MAX_AGE_SECONDS}; SameSite=Lax`;
}

export function getReferralCookie(): string {
  if (typeof document === "undefined") return "";
  return parseReferralCookie(document.cookie);
}

export function setReferralCookie(ref: string): void {
  if (typeof document === "undefined" || !ref.trim()) return;
  document.cookie = buildReferralCookieString(ref);
}

// URL ref always wins (it's the freshest signal); the cookie is only the
// fallback for when ?ref= is missing from the current URL. Pass an explicit
// cookieValue to test this without touching document.cookie.
export function resolveReferralCode(urlRef: string, cookieValue?: string): string {
  const trimmed = urlRef.trim();
  if (trimmed) return trimmed;
  return cookieValue !== undefined ? cookieValue : getReferralCookie();
}

// A client-supplied repCode is never trusted on its own — every consumer
// (the associate recruitment landing page, /join, /join-agent) must resolve
// it server-side via rep.getByCode first (never trust the query string for
// display or attribution), then run the RESULT through this check before
// treating it as a valid, creditable referrer. rep.getByCode does not filter
// by status (RepDashboard needs it to work for an inactive rep viewing their
// OWN account), so an invalid/nonexistent (`null`), disabled/suspended, or
// rejected referrer must be caught here, at the point of USE — the caller
// falls back to organic/no-referrer (never shows a broken "Invited by"
// state, never forwards a bad ref into registration attribution).
export function isCreditableReferrer(rep: { status?: string | null } | null | undefined): boolean {
  return !!rep && rep.status === "active";
}

// Builds a per-associate referral link that always credits the CURRENT
// repCode, even if the admin-configured base URL already has its own ref
// query param baked in (e.g. copied from a test/QA link while setting up
// the URL in Admin > Referral Links). URLSearchParams.set() replaces
// rather than duplicates the key - a stale baked-in ref can never shadow
// the real associate's code the way naive string concatenation could (a
// duplicated ?ref=...&ref=... resolves to the FIRST value everywhere
// downstream that reads it with URLSearchParams.get("ref"), including
// this app's own checkout param parsing).
export function withRefCode(baseUrl: string, repCode: string): string {
  try {
    const url = new URL(baseUrl);
    url.searchParams.set("ref", repCode);
    return url.toString();
  } catch {
    return baseUrl.includes("?") ? `${baseUrl}&ref=${repCode}` : `${baseUrl}?ref=${repCode}`;
  }
}

// Builds the "Get Started" CTA target for the associate recruitment landing
// page (/associates) — /join-agent (the registration flow) with ?ref=
// attached ONLY when the referrer has already been verified server-side
// (isCreditableReferrer). Never forwards a client-supplied repCode that
// hasn't passed that check.
//
// Client decision (2026-08-13): a recruiter link is now REQUIRED to
// register - self-registration must always be traceable to a real upline/
// agency (see BC-607). A missing, tampered, or unverifiable ref no longer
// falls through to a bare /join-agent link (which would just dead-end at
// the server's rejection anyway) - it returns null so the caller can show
// an "invalid invite link" state instead of a broken CTA.
export function buildJoinAgentHref(refCode: string, isValidReferrer: boolean): string | null {
  if (!isValidReferrer || !refCode.trim()) return null;
  return `/join-agent?ref=${encodeURIComponent(refCode.trim())}`;
}
